import {
  Component,
  computed,
  effect,
  input,
  signal,
  viewChild,
  inject,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { getOffsetDate, startOfDay } from '@app/core/util/date_utils';

enum AlertFilterValue {
  UnreadAndRead = 'unreadAndRead',
  UnreadOnly = 'unreadOnly',
  Archived = 'archived',
}

enum AlertBulkActionValue {
  MarkAllRead = 'markAllRead',
  ArchiveAllRead = 'archiveAllRead',
  ArchiveAll = 'archiveAll',
}

interface IAlertFilterOption {
  label: string;
  value: AlertFilterValue;
  iconUrl: string;
}

interface IAlertBulkActionOption {
  label: string;
  value: AlertBulkActionValue;
  iconUrl: string;
}

@Component({
  selector: 'app-alerts-menu',
  templateUrl: './alerts-menu.component.html',
  standalone: false,
})
export class AlertsMenuComponent {
  private _router: Router = inject(Router);
  layout = input<'menu' | 'standalone'>('standalone');
  alertsCountChange = output<number>();

  alertPopover = viewChild<Popover>('alertsPopover');
  alertFilterPopover = viewChild<Popover>('alertFilterPopover');
  alertBulkActionsPopover = viewChild<Popover>('alertBulkActionsPopover');

  alertFilterPopoverVisible = signal<boolean>(false);
  alertBulkActionsPopoverVisible = signal<boolean>(false);
  alertsPopoverVisible = signal<boolean>(false);
  drawerVisible = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  alertsCount = computed(() => this.alerts().length);
  activeAlertsCount = computed(
    () => this.alerts().filter((alert) => !alert?.read_at).length,
  );

  alertsTitle = computed(() => {
    return this.selectedFilter()?.value === AlertFilterValue.Archived
      ? 'Archived Alerts'
      : 'Critical Alerts';
  });

  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.alertsCount() >= 6,
  );

  filteredAlerts = computed(() => {
    const list = this.alerts();

    const filter: AlertFilterValue = this.selectedFilter()?.value;
    if (!filter) return list.filter((a) => !a.archived_at);
    switch (filter) {
      case AlertFilterValue.UnreadOnly:
        return list.filter((a: any) => !a.archived_at && !a.read_at);
      case AlertFilterValue.Archived:
        return list.filter((a: any) => !!a.archived_at);
      default:
        return list.filter((a: any) => !a.archived_at);
    }
  });

  filteredAlertsCount = computed(() => this.filteredAlerts().length);

  alertsToday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    return this.filteredAlerts().filter(
      (alert) => alert.sent_at >= startOfToday,
    );
  });

  alertsYesterday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredAlerts().filter(
      (alert) =>
        alert.sent_at >= startOfYesterday && alert.sent_at < startOfToday,
    );
  });

  alertsOlder = computed(() => {
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredAlerts().filter(
      (alert) => alert.sent_at < startOfYesterday,
    );
  });

  showFilterIndicator = computed(() => {
    const value = this.selectedFilter()?.value;
    return (
      value === AlertFilterValue.UnreadOnly ||
      value === AlertFilterValue.Archived
    );
  });

  filterOptions: IAlertFilterOption[] = [
    {
      label: 'Unread & read',
      value: AlertFilterValue.UnreadAndRead,
      iconUrl: 'assets/icons/general/action-list.svg',
    },
    {
      label: 'Unread only',
      value: AlertFilterValue.UnreadOnly,
      iconUrl: 'assets/icons/general/communication-unread.svg',
    },
    {
      label: 'Archived',
      value: AlertFilterValue.Archived,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  bulkActionOptions: IAlertBulkActionOption[] = [
    {
      label: 'Mark all as read',
      value: AlertBulkActionValue.MarkAllRead,
      iconUrl: 'assets/icons/general/system-feedback-success.svg',
    },
    {
      label: 'Archive all',
      value: AlertBulkActionValue.ArchiveAll,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
    {
      label: 'Archive all read',
      value: AlertBulkActionValue.ArchiveAllRead,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  selectedFilter = signal<IAlertFilterOption | null>(this.filterOptions[0]);
  selectedBulkAction = signal<IAlertBulkActionOption | null>(null);

  alerts = signal<any[]>([
    {
      id: '1',
      title: 'Alert 1, pallet data has been updated',
      body: 'A new file updates 15 pallets with new values. Review updates. This is a long alert body to test the overflow of the alert menu. ',
      read_at: null,
      sent_at: new Date(),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '2',
      title: 'Alert 2. Long, title, long title, long title',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: new Date(),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/vessel.svg',
      targetUrl: '/account/dashboard',
    },
    {
      id: '3',
      title: 'Short title',
      body: 'Short body ',
      read_at: null,
      sent_at: getOffsetDate(1),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '4',
      title: 'Long title, long title, long title, long title',
      body: 'Short body ',
      read_at: null,
      sent_at: getOffsetDate(1),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/vessel.svg',
      targetUrl: '/account/dashboard',
    },
    {
      id: '5',
      title: 'Alert 5',
      body: 'Long long long long long long body',
      read_at: null,
      sent_at: getOffsetDate(2),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/vessel.svg',
      targetUrl: '/account/dashboard',
    },
    {
      id: '6',
      title: 'Alert 6',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '7',
      title: 'Alert 7',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '8',
      title: 'Alert 8',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '9',
      title: 'Alert 9',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '10',
      title: 'Alert 10',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
    {
      id: '11',
      title: 'Alert 11',
      body: 'A new file updates 15 pallets with new values. Review updates ',
      read_at: null,
      sent_at: getOffsetDate(3),
      archived_at: null,
      iconPath: 'assets/icons/dashboard/cards/pallet.svg',
      targetUrl: '/account/stock',
    },
  ]);

  constructor() {
    effect(() => {
      this.alertsCountChange.emit(this.alertsCount());
    });

    effect(() => {
      if (!this.drawerVisible()) {
        this.selectedFilter.set(this.filterOptions[0]);
      }
    });
  }

  onStandaloneButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.isSidepanelLayout()) {
      this.drawerVisible.update((visible: boolean) => !visible);
      this.alertPopover()?.hide();
    } else {
      this.alertPopover().toggle(event);
      this.drawerVisible.set(false);
    }
  }

  onAlertsPopoverShow(): void {
    this.alertsPopoverVisible.set(true);
  }

  onAlertsPopoverHide(): void {
    this.alertsPopoverVisible.set(false);
    // Reset the selected filter when the alerts popover is hidden
    this.selectedFilter.set(this.filterOptions[0]);
  }

  onAlertFilterPopoverShow(): void {
    this.alertFilterPopoverVisible.set(true);
  }

  onAlertFilterPopoverHide(): void {
    this.alertFilterPopoverVisible.set(false);
  }

  onAlertBulkActionsPopoverShow(): void {
    this.alertBulkActionsPopoverVisible.set(true);
  }

  onAlertBulkActionsPopoverHide(): void {
    this.alertBulkActionsPopoverVisible.set(false);
  }

  onFilterButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertBulkActionsPopoverVisible()) {
      this.alertBulkActionsPopover().hide();
    }

    this.alertFilterPopover().toggle(event);
  }

  onFilterSelectionChange(selectedFilter: IAlertFilterOption | null): void {
    if (!selectedFilter) return;

    this.selectedFilter.set(selectedFilter);
    this.alertFilterPopover()?.hide();
  }

  onBulkActionsButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertFilterPopoverVisible()) {
      this.alertFilterPopover().hide();
    }

    this.alertBulkActionsPopover()?.toggle(event);
  }

  onBulkActionSelectionChange(selected: IAlertBulkActionOption | null): void {
    const value: AlertBulkActionValue | undefined = selected?.value;
    if (!value) return;

    switch (value) {
      case AlertBulkActionValue.MarkAllRead:
        this.onMarkAllAsRead();
        break;
      case AlertBulkActionValue.ArchiveAllRead:
        this.onArchiveAllRead();
        break;
      case AlertBulkActionValue.ArchiveAll:
        this.onArchiveAll();
        break;
    }

    this.selectedBulkAction.set(null);
    this.alertBulkActionsPopover()?.hide();
  }

  onMarkAllAsRead(): void {
    this.alerts.update((alerts) =>
      alerts.map((a) => ({ ...a, read_at: a.read_at ?? new Date() })),
    );
  }

  onArchiveAllRead(): void {
    this.alerts.update((alerts) =>
      alerts.map((a) =>
        a.read_at && !a.archived_at ? { ...a, archived_at: new Date() } : a,
      ),
    );
  }

  onArchiveAll(): void {
    const date: Date = new Date();

    this.alerts.update((alerts) =>
      alerts.map((a) => ({
        ...a,
        archived_at: a.archived_at ?? date,
        read_at: a.read_at ?? date,
      })),
    );
  }

  openSettings(): void {
    this._router.navigate([
      '/account/notification-preferences/general-preferences',
    ]);

    this._hideView();
  }

  onToggleMarkAsRead(alert: any): void {
    if (!alert) return;
    const isRead: boolean = !!alert.read_at;
    this.alerts.update((alerts) =>
      alerts.map((a) =>
        a.id === alert.id ? { ...a, read_at: isRead ? null : new Date() } : a,
      ),
    );
  }

  onAlertArchive(alert: any): void {
    if (!alert) return;
    const date: Date = new Date();
    this.alerts.update((alerts) =>
      alerts.map((a) =>
        a.id === alert.id ? { ...a, archived_at: date, read_at: date } : a,
      ),
    );
  }

  onAlertClick(alert: any): void {
    if (!alert) return;
    this.alerts.update((alerts) =>
      alerts.map((a) =>
        a.id === alert.id ? { ...a, read_at: new Date() } : a,
      ),
    );

    if (!alert?.targetUrl) return;
    this._router.navigate([alert.targetUrl]);
    this._hideView();
  }

  private _hideView(): void {
    this.alertPopover().hide();
    this.alertFilterPopover().hide();
    this.alertBulkActionsPopover().hide();

    this.drawerVisible.set(false);
  }
}
